import { useState, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, Upload, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { EmbroideryConverter } from '@/components/EmbroideryConverter';

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  enhancedUrl?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  originalDpi?: number;
  enhancedDpi?: number;
  originalQualityLevel?: string;
  enhancedQualityLevel?: string;
}

export default function PublicImageEnhancer() {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [enhancementType, setEnhancementType] = useState<'dpi' | 'quality' | 'both'>('both');
  const [dpiTarget, setDpiTarget] = useState(300);
  const [qualityLevel, setQualityLevel] = useState<'standard' | 'hd' | 'ultra'>('hd');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Watermark settings
  const [enableWatermark, setEnableWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState('© My Brand');
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(50);
  const [watermarkSize, setWatermarkSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [watermarkColor, setWatermarkColor] = useState('#FFFFFF');
  const [watermarkBackground, setWatermarkBackground] = useState(true);

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    
    if (images.length + fileArray.length > 10) {
      toast.error('Maximum 10 images allowed per session');
      return;
    }

    const newImages: UploadedImage[] = fileArray.map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0
    }));

    setImages(prev => [...prev, ...newImages]);
    toast.success(`Added ${fileArray.length} image(s)`);
  }, [images.length]);

  const removeImage = (id: string) => {
    setImages(prev => {
      const image = prev.find(img => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter(img => img.id !== id);
    });
  };

  const enhanceImages = async () => {
    if (images.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setIsProcessing(true);

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      
      try {
        setImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, status: 'processing', progress: 10 } : img
        ));

        // Convert and downscale image to speed up processing
        const base64Data = await (async () => {
          // Read file as data URL first
          const readAsDataURL = (file: File) => new Promise<string>((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.readAsDataURL(file);
          });

          const src = await readAsDataURL(image.file);

          // Compute a sensible max dimension to reduce payload size
          const maxDim = (enhancementType === 'dpi' && dpiTarget >= 600) ? 1600 : 1280;

          // Load image element
          const imgEl = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = (e) => reject(e);
            el.src = src;
          });

          const largestSide = Math.max(imgEl.width, imgEl.height);
          const scale = Math.min(1, maxDim / (largestSide || maxDim));

          // If no resize needed, return original
          if (scale >= 1) return src;

          // Draw to canvas at reduced resolution
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(imgEl.width * scale);
          canvas.height = Math.round(imgEl.height * scale);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
          }

          // Choose output format based on original file type
          const mime = image.file.type.includes('png') ? 'image/png' : 'image/jpeg';
          // Use reasonable JPEG quality to cut size while preserving detail
          return canvas.toDataURL(mime, mime === 'image/jpeg' ? 0.9 : undefined as any);
        })();

        setImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, progress: 30 } : img
        ));

        // Call enhancement function
        const { data, error } = await supabase.functions.invoke('enhance-image-quality', {
          body: {
            imageUrl: base64Data,
            enhancementType,
            dpiTarget: enhancementType === 'dpi' || enhancementType === 'both' ? dpiTarget : undefined,
            qualityLevel: enhancementType === 'quality' || enhancementType === 'both' ? qualityLevel : undefined,
            watermark: enableWatermark ? {
              text: watermarkText,
              position: watermarkPosition,
              opacity: watermarkOpacity,
              size: watermarkSize,
              color: watermarkColor,
              background: watermarkBackground
            } : undefined
          }
        });

        if (error) throw error;

        setImages(prev => prev.map(img => 
          img.id === image.id 
            ? { 
                ...img, 
                status: 'completed', 
                progress: 100, 
                enhancedUrl: data.enhancedImageUrl,
                originalDpi: 72,
                enhancedDpi: data.dpiTarget || dpiTarget,
                originalQualityLevel: 'standard',
                enhancedQualityLevel: qualityLevel
              }
            : img
        ));

        toast.success(`Enhanced: ${image.file.name}`);
        
      } catch (error: any) {
        console.error('Enhancement error:', error);
        setImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, status: 'failed', progress: 0 } : img
        ));
        toast.error(`Failed: ${image.file.name}`);
      }

      // Small delay between images
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsProcessing(false);
    toast.success('All images processed!');
  };

  const downloadAll = () => {
    const completedImages = images.filter(img => img.enhancedUrl);
    
    completedImages.forEach((img, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = img.enhancedUrl!;
        link.download = `enhanced-${img.file.name}`;
        link.click();
      }, index * 200);
    });

    toast.success(`Downloading ${completedImages.length} enhanced images`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-900/20 via-background to-background">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Free Image Tools
          </h1>
          <p className="text-lg text-muted-foreground">
            Professional image enhancement and embroidery conversion tools
          </p>
        </div>

        <Tabs defaultValue="enhance" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="enhance">Image Enhancement</TabsTrigger>
            <TabsTrigger value="embroidery">Embroidery Conversion</TabsTrigger>
          </TabsList>

          <TabsContent value="enhance">
            <Card className="p-8 bg-gradient-to-br from-purple-500/5 to-blue-500/5 border-purple-500/20">
          {/* Upload Area */}
          <div
            className="border-2 border-dashed border-purple-500/30 rounded-lg p-12 mb-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('file-upload')?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFileUpload(e.dataTransfer.files);
            }}
          >
            <Upload className="w-12 h-12 mx-auto mb-4 text-purple-400" />
            <p className="text-lg mb-2">Drop files here or click to browse</p>
            <p className="text-sm text-muted-foreground">
              PNG, JPG, WebP (Max 10 images, 10MB each)
            </p>
            <input
              id="file-upload"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
          </div>

          {/* Enhancement Options */}
          <div className="space-y-8 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Label className="mb-3 block">Enhancement Type</Label>
              <RadioGroup value={enhancementType} onValueChange={(v) => setEnhancementType(v as any)}>
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="dpi" id="pub-dpi" />
                  <Label htmlFor="pub-dpi" className="cursor-pointer">DPI Enhancement</Label>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="quality" id="pub-quality" />
                  <Label htmlFor="pub-quality" className="cursor-pointer">Quality Boost</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="pub-both" />
                  <Label htmlFor="pub-both" className="cursor-pointer">Both (Recommended)</Label>
                </div>
              </RadioGroup>
            </div>

            {(enhancementType === 'dpi' || enhancementType === 'both') && (
              <div>
                <Label className="mb-3 block">Target DPI: {dpiTarget}</Label>
                <Slider
                  value={[dpiTarget]}
                  onValueChange={(v) => setDpiTarget(v[0])}
                  min={150}
                  max={600}
                  step={150}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>150</span>
                  <span>300</span>
                  <span>600</span>
                </div>
              </div>
            )}
            
            {(enhancementType === 'quality' || enhancementType === 'both') && (
              <div>
                <Label className="mb-3 block">Quality Level</Label>
                <RadioGroup value={qualityLevel} onValueChange={(v) => setQualityLevel(v as any)}>
                  <div className="flex items-center space-x-2 mb-2">
                    <RadioGroupItem value="standard" id="quality-standard" />
                    <Label htmlFor="quality-standard" className="cursor-pointer">Standard</Label>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <RadioGroupItem value="hd" id="quality-hd" />
                    <Label htmlFor="quality-hd" className="cursor-pointer">HD (Recommended)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ultra" id="quality-ultra" />
                    <Label htmlFor="quality-ultra" className="cursor-pointer">Ultra</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
            </div>

            {/* Watermark Section */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-lg font-semibold">Watermark Settings</Label>
                  <p className="text-sm text-muted-foreground mt-1">Add custom watermark to your enhanced images</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="enable-watermark" className="cursor-pointer">Enable</Label>
                  <input
                    id="enable-watermark"
                    type="checkbox"
                    checked={enableWatermark}
                    onChange={(e) => setEnableWatermark(e.target.checked)}
                    className="w-5 h-5 cursor-pointer accent-purple-500"
                  />
                </div>
              </div>

              {enableWatermark && (
                <div className="grid md:grid-cols-2 gap-6 mt-4">
                  {/* Watermark Text */}
                  <div className="md:col-span-2">
                    <Label className="mb-2 block">Watermark Text</Label>
                    <input
                      type="text"
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder="Enter watermark text"
                      className="w-full px-4 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Position */}
                  <div>
                    <Label className="mb-3 block">Position</Label>
                    <RadioGroup value={watermarkPosition} onValueChange={(v) => setWatermarkPosition(v as any)}>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="top-left" id="pos-tl" />
                          <Label htmlFor="pos-tl" className="cursor-pointer text-xs">Top Left</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="top-center" id="pos-tc" />
                          <Label htmlFor="pos-tc" className="cursor-pointer text-xs">Top Center</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="top-right" id="pos-tr" />
                          <Label htmlFor="pos-tr" className="cursor-pointer text-xs">Top Right</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="center-left" id="pos-cl" />
                          <Label htmlFor="pos-cl" className="cursor-pointer text-xs">Center Left</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="center" id="pos-c" />
                          <Label htmlFor="pos-c" className="cursor-pointer text-xs">Center</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="center-right" id="pos-cr" />
                          <Label htmlFor="pos-cr" className="cursor-pointer text-xs">Center Right</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="bottom-left" id="pos-bl" />
                          <Label htmlFor="pos-bl" className="cursor-pointer text-xs">Bottom Left</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="bottom-center" id="pos-bc" />
                          <Label htmlFor="pos-bc" className="cursor-pointer text-xs">Bottom Center</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="bottom-right" id="pos-br" />
                          <Label htmlFor="pos-br" className="cursor-pointer text-xs">Bottom Right</Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Size */}
                  <div>
                    <Label className="mb-3 block">Font Size</Label>
                    <RadioGroup value={watermarkSize} onValueChange={(v) => setWatermarkSize(v as any)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="small" id="size-small" />
                        <Label htmlFor="size-small" className="cursor-pointer">Small</Label>
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="medium" id="size-medium" />
                        <Label htmlFor="size-medium" className="cursor-pointer">Medium</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="large" id="size-large" />
                        <Label htmlFor="size-large" className="cursor-pointer">Large</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Opacity */}
                  <div>
                    <Label className="mb-3 block">Opacity: {watermarkOpacity}%</Label>
                    <Slider
                      value={[watermarkOpacity]}
                      onValueChange={(v) => setWatermarkOpacity(v[0])}
                      min={10}
                      max={100}
                      step={5}
                    />
                  </div>

                  {/* Color */}
                  <div>
                    <Label className="mb-2 block">Text Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={watermarkColor}
                        onChange={(e) => setWatermarkColor(e.target.value)}
                        className="w-16 h-10 cursor-pointer rounded border border-border"
                      />
                      <input
                        type="text"
                        value={watermarkColor}
                        onChange={(e) => setWatermarkColor(e.target.value)}
                        placeholder="#FFFFFF"
                        className="flex-1 px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Background */}
                  <div className="flex items-center gap-3">
                    <input
                      id="watermark-bg"
                      type="checkbox"
                      checked={watermarkBackground}
                      onChange={(e) => setWatermarkBackground(e.target.checked)}
                      className="w-5 h-5 cursor-pointer accent-purple-500"
                    />
                    <div>
                      <Label htmlFor="watermark-bg" className="cursor-pointer">Add Background</Label>
                      <p className="text-xs text-muted-foreground">Semi-transparent background for better visibility</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-8">
            <Button
              onClick={enhanceImages}
              disabled={isProcessing || images.length === 0}
              className="flex-1"
              size="lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {isProcessing ? 'Enhancing...' : `Enhance ${images.length} Image(s)`}
            </Button>
            
            <Button
              onClick={downloadAll}
              disabled={images.filter(img => img.enhancedUrl).length === 0}
              variant="outline"
              size="lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Download All
            </Button>
          </div>

          {/* Image Grid */}
          {images.length > 0 && (
            <div className="space-y-6">
              {images.map((image) => (
                <div key={image.id} className="relative">
                  {image.status === 'completed' && image.enhancedUrl ? (
                    // Before/After Comparison View
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm">{image.file.name}</h3>
                        <Button
                          onClick={() => removeImage(image.id)}
                          variant="ghost"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-2 block text-xs text-muted-foreground">
                            Before (Original)
                          </Label>
                          <div className="aspect-square rounded-lg overflow-hidden border border-border">
                            <img
                              src={image.preview}
                              alt="Original"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="mt-2 space-y-1 text-xs">
                            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                              <span className="text-muted-foreground">DPI:</span>
                              <span className="font-medium">{image.originalDpi || 72}</span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                              <span className="text-muted-foreground">Quality:</span>
                              <span className="font-medium capitalize">{image.originalQualityLevel || 'standard'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="mb-2 block text-xs text-purple-400 font-semibold">
                            After (Enhanced) ✨
                          </Label>
                          <div className="aspect-square rounded-lg overflow-hidden border-2 border-purple-500">
                            <img
                              src={image.enhancedUrl}
                              alt="Enhanced"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="mt-2 space-y-1 text-xs">
                            <div className="flex justify-between items-center p-2 bg-purple-500/10 border border-purple-500/20 rounded">
                              <span className="text-purple-400">DPI:</span>
                              <span className="font-bold text-purple-400">{image.enhancedDpi || dpiTarget}</span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-purple-500/10 border border-purple-500/20 rounded">
                              <span className="text-purple-400">Quality:</span>
                              <span className="font-bold text-purple-400 capitalize">{image.enhancedQualityLevel || qualityLevel}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex gap-2">
                        <Button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = image.enhancedUrl!;
                            link.download = `enhanced-${image.file.name}`;
                            link.click();
                          }}
                          size="sm"
                          className="flex-1"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Enhanced
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Processing or Pending View
                    <div className="group bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm">{image.file.name}</h3>
                        <Button
                          onClick={() => removeImage(image.id)}
                          variant="ghost"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="aspect-video rounded-lg overflow-hidden bg-black/20 relative">
                        <img
                          src={image.preview}
                          alt="Upload"
                          className="w-full h-full object-cover"
                        />
                        
                        {image.status === 'processing' && (
                          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
                            <div className="text-white text-sm font-medium">Enhancing...</div>
                            <Progress value={image.progress} className="w-3/4" />
                          </div>
                        )}
                        
                        {image.status === 'failed' && (
                          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                            <div className="bg-red-500 text-white text-xs px-3 py-1.5 rounded">
                              Enhancement Failed
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Privacy Notice */}
          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-center text-muted-foreground">
              🔒 Your privacy matters: All images are processed securely and automatically deleted after 24 hours.
            </p>
          </div>
            </Card>
          </TabsContent>

          <TabsContent value="embroidery">
            <EmbroideryConverter />
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  );
}